import React, { useEffect, useRef, useState } from "react";
import {
  ActivityLog,
  formatRole
} from "../../utils/hooks/multi-tenant/invitation";
import RichTextRenderer from "../common/RichTextRenderer";

interface ActivityLogsTableProps {
  logs: ActivityLog[];
  isLoading: boolean;
  isError: boolean;
}

const ActivityLogsTable: React.FC<ActivityLogsTableProps> = ({
  logs,
  isLoading,
  isError
}) => {
  const [expandedLogId, setExpandedLogId] = useState<number | null>(null);
  // pagination handled by parent; show all provided rows
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [contentWidth, setContentWidth] = useState<number>(0);
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const [scrollLeft, setScrollLeft] = useState<number>(0);
  const [isDragging, setIsDragging] = useState<boolean>(false);

  useEffect(() => {
    const updateWidth = () => {
      if (scrollRef.current) {
        setContentWidth(scrollRef.current.scrollWidth);
        setContainerWidth(scrollRef.current.clientWidth);
        setScrollLeft(scrollRef.current.scrollLeft);
      }
    };
    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, [logs]);

  // Parent controls pagination; no local slicing

  const onTopScroll = () => {
    if (scrollRef.current) {
      setScrollLeft(scrollRef.current.scrollLeft);
    }
  };

  const onThumbDrag = (clientX: number) => {
    if (!scrollRef.current) return;
    const track = scrollRef.current.getBoundingClientRect();
    const usableTrack = containerWidth - thumbWidth;
    const relative = Math.min(Math.max(clientX - track.left - thumbWidth / 2, 0), usableTrack);
    const newScrollLeft = (relative / usableTrack) * (contentWidth - containerWidth);
    scrollRef.current.scrollLeft = newScrollLeft;
    setScrollLeft(newScrollLeft);
  };

  const onMouseMove = (e: MouseEvent) => {
    if (!isDragging) return;
    onThumbDrag(e.clientX);
  };

  const onMouseUp = () => setIsDragging(false);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", onMouseMove);
      window.addEventListener("mouseup", onMouseUp, { once: true });
    }
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [isDragging]);

  const thumbWidth = Math.max(
    containerWidth && contentWidth ? (containerWidth * containerWidth) / contentWidth : 0,
    40
  );
  const maxThumbLeft = Math.max(containerWidth - thumbWidth, 0);
  const thumbLeft = contentWidth > containerWidth
    ? (scrollLeft / (contentWidth - containerWidth)) * maxThumbLeft
    : 0;

  return (
    <>
    <div className="rounded-lg border border-gray-200 overflow-hidden">
      <div
        ref={scrollRef}
        onScroll={onTopScroll}
        className="overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-[#F9FAFB]">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-600">
              Username
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-600">
              Email
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-600">
              Role
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-600">
              Action
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-600">
              Module
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-600">
              Date
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white">
          {isLoading ? (
            <tr>
              <td
                colSpan={6}
                className="px-6 py-4 text-center text-sm text-gray-500"
              >
                Loading activity logs...
              </td>
            </tr>
          ) : isError ? (
            <tr>
              <td
                colSpan={6}
                className="px-6 py-4 text-center text-sm text-red-500"
              >
                Failed to load activity logs
              </td>
            </tr>
          ) : logs.length === 0 ? (
            <tr>
              <td
                colSpan={6}
                className="px-6 py-4 text-center text-sm text-gray-500"
              >
                No activity logs found
              </td>
            </tr>
          ) : (
            logs.map(log => (
              <tr
                key={log.id}
                className="transition-colors duration-200 hover:bg-gray-50"
              >
                <td className="whitespace-nowrap px-6 py-4 text-[12px] text-gray-500">
                  {log.user.username}
                </td>
                <td
                  className="cursor-pointer whitespace-nowrap px-6 py-4 text-[12px] text-gray-500"
                  style={{
                    maxWidth: "250px",
                    whiteSpace: expandedLogId === log.id ? "normal" : "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis"
                  }}
                  onClick={() =>
                    setExpandedLogId(prev => (prev === log.id ? null : log.id))
                  }
                  title={log.user.email}
                >
                  {expandedLogId === log.id
                    ? log.user.email
                    : log.user.email.length > 20
                    ? `${log.user.email.substring(0, 20)}...`
                    : log.user.email}
                </td>
                <td className="whitespace-nowrap px-6 py-4 text-[12px] text-gray-500">
                  {formatRole(log.role)}
                </td>
                <td
                  className="cursor-pointer px-6 py-4 text-[12px] text-gray-900"
                  style={{
                    maxWidth: "300px",
                    whiteSpace: expandedLogId === log.id ? "normal" : "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis"
                  }}
                  onClick={() =>
                    setExpandedLogId(prev => (prev === log.id ? null : log.id))
                  }
                  title={log.action}
                >
                  {expandedLogId === log.id ? (
                    <RichTextRenderer content={log.action} />
                  ) : log.action.length > 100 ? (
                    `${log.action.substring(0, 100)}...`
                  ) : (
                    <RichTextRenderer content={log.action} />
                  )}
                </td>

                <td className="whitespace-nowrap px-6 py-4 text-[12px] text-gray-500">
                  {log.module}
                </td>
                <td className="whitespace-nowrap px-6 py-4 text-[12px] text-gray-500">
                  {new Date(log.created_at)
                    .toLocaleString("en-GB", {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                      second: "2-digit",
                      hour12: false
                    })
                    .replace(",", "")}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      </div>
      {/* Always-visible custom horizontal scrollbar */}
      <div className="mt-2 h-3 bg-gray-200 rounded relative select-none mx-2">
        <div
          className="absolute top-0 h-3 bg-gray-400 rounded cursor-pointer"
          style={{ width: `${thumbWidth}px`, left: `${thumbLeft}px` }}
          onMouseDown={e => {
            e.preventDefault();
            setIsDragging(true);
          }}
        />
      </div>
    </div>
    {/* Pagination is handled by parent */}
    </>
  );
};

export default ActivityLogsTable;
